import type { Ratecard } from '@clockwise/clients/ratecards';
import type { ServiceRole } from '@clockwise/clients/service-roles';
import { ApiError, isoDateString } from '@clockwise/shared';
import { DatePicker } from '@clockwise/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, type UseFormReturn, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { CurrencyInput } from '@/components/custom/currency-input';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { ratecardsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { rateError } from '@/lib/rate-validation';

const todayIso = () => new Date().toISOString().slice(0, 10);
const isoDate = isoDateString();

const RateLineSchema = z.object({
  serviceRoleId: z.string().uuid(),
  // CurrencyInput holds the in-progress string itself and emits a number, so
  // the field is a plain number here. Bounds match the SOW T&M rate (> 0, up
  // to MAX_TM_RATE) via the shared rateError — both surfaces set T&M rates.
  rate: z.number().superRefine((n, ctx) => {
    const message = rateError(n);
    if (message) ctx.addIssue({ code: z.ZodIssueCode.custom, message });
  }),
});

const RatecardFormSchema = z
  .object({
    name: z.string().trim().min(1, 'Required').max(200),
    effectiveFrom: isoDate,
    // Optional upper bound; '' = open-ended (the DatePicker clears to '').
    effectiveTo: z.union([isoDate, z.literal('')]).optional(),
    lines: z.array(RateLineSchema).min(1),
  })
  .refine((d) => !d.effectiveTo || d.effectiveTo > d.effectiveFrom, {
    message: 'Effective to must be after effective from',
    path: ['effectiveTo'],
  });

type RatecardFormValues = z.infer<typeof RatecardFormSchema>;

const RATECARD_FORM_FIELD_NAMES = ['name', 'effectiveFrom', 'effectiveTo'] as const;
function isTopLevelField(path: string): path is (typeof RATECARD_FORM_FIELD_NAMES)[number] {
  return (RATECARD_FORM_FIELD_NAMES as readonly string[]).includes(path);
}

/** Parse `lines.<index>.<subpath>` → numeric index, or null. */
function parseLineIndex(path: string): { index: number; subpath: string } | null {
  const match = path.match(/^lines\.(\d+)(?:\.(.+))?$/);
  if (!match || match[1] === undefined) return null;
  return { index: Number(match[1]), subpath: match[2] ?? '' };
}

interface RatecardFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Active service roles in the tenant — every row will be presented for pricing. */
  serviceRoles: ServiceRole[];
  /** The card being edited, or null to create a new one. */
  current: Ratecard | null;
}

function initialValues(current: Ratecard | null, serviceRoles: ServiceRole[]): RatecardFormValues {
  const byRole = new Map(current?.lines.map((l) => [l.serviceRoleId, l.rate]) ?? []);
  return {
    name: current?.name ?? '',
    effectiveFrom: current?.effectiveFrom ?? todayIso(),
    effectiveTo: current?.effectiveTo ?? '',
    lines: serviceRoles.map((r) => ({ serviceRoleId: r.id, rate: byRole.get(r.id) ?? 0 })),
  };
}

export function RatecardFormSheet({
  open,
  onOpenChange,
  serviceRoles,
  current,
}: RatecardFormSheetProps) {
  const isEdit = current !== null;
  const queryClient = useQueryClient();

  const form = useForm<RatecardFormValues>({
    resolver: zodResolver(RatecardFormSchema),
    defaultValues: initialValues(current, serviceRoles),
    mode: 'onSubmit',
  });

  // Reset whenever the sheet opens or the current/roles shape changes —
  // the lines array's order/length is keyed off serviceRoles.
  useEffect(() => {
    if (open) form.reset(initialValues(current, serviceRoles));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, current?.id, serviceRoles.length]);

  const mutation = useMutation({
    mutationFn: async (values: RatecardFormValues) => {
      const lines = values.lines.map((l) => ({
        serviceRoleId: l.serviceRoleId,
        rate: l.rate,
      }));
      const body = {
        name: values.name,
        effectiveFrom: values.effectiveFrom,
        effectiveTo: values.effectiveTo ? values.effectiveTo : null,
        lines,
      };
      if (isEdit && current) {
        // Send the updatedAt we loaded so the server can reject a stale
        // overwrite (optimistic concurrency).
        return ratecardsApi.update(current.id, { ...body, expectedUpdatedAt: current.updatedAt });
      }
      return ratecardsApi.create(body);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Rate card updated' : 'Rate card created');
      queryClient.invalidateQueries({ queryKey: queryKeys.ratecards.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      onOpenChange(false);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.fields?.length) {
        let mapped = 0;
        for (const fe of err.fields) {
          if (isTopLevelField(fe.path)) {
            form.setError(fe.path, { message: fe.message });
            mapped++;
            continue;
          }
          const line = parseLineIndex(fe.path);
          if (line) {
            // Map `lines.<i>.serviceRoleId` and `lines.<i>.rate` to the
            // rate input — the serviceRoleId is a hidden field the user
            // can't act on, so server-side "unknown/duplicate role" errors
            // surface on the visible rate field for that row.
            form.setError(`lines.${line.index}.rate`, { message: fe.message });
            mapped++;
            continue;
          }
          if (fe.path === 'lines' || fe.path === '') {
            form.setError('root.lines', { message: fe.message });
            mapped++;
          }
        }
        if (mapped === 0) {
          toast.error(err.message);
        } else {
          toast.error('Please fix the highlighted fields');
        }
        return;
      }
      // A tenant-wide overlap conflict carries a field (`effectiveFrom`) and is
      // handled by the branch above. A fieldless 409 is a concurrency conflict
      // (the card changed under us) — there's no field to anchor it to, so just
      // surface the message.
      if (err instanceof ApiError && err.httpStatus === 409) {
        toast.error(err.message);
        return;
      }
      toast.error(err instanceof Error ? err.message : 'Save failed');
    },
  });

  const onSubmit = form.handleSubmit((v) => mutation.mutate(v));

  const { requestOpenChange, discardDialog } = useUnsavedChangesGuard({
    open,
    isDirty: form.formState.isDirty,
    isSubmitting: mutation.isPending,
    onOpenChange,
  });

  return (
    <>
      <Sheet open={open} onOpenChange={requestOpenChange}>
        <SheetContent className="flex flex-col gap-0 sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>{isEdit ? 'Edit rate card' : 'Create rate card'}</SheetTitle>
            <SheetDescription>
              {isEdit
                ? 'Update this card in place. Only one card may be active on any date.'
                : 'Set T&M rates for every active service role. To change rates later, create a new card whose dates follow this one.'}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 space-y-4 overflow-y-auto px-4 py-3">
              <HeaderFields form={form} />
              <LinesEditor form={form} serviceRoles={serviceRoles} />
            </div>

            <SheetFooter className="flex-row justify-end border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => requestOpenChange(false)}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending || serviceRoles.length === 0}>
                {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create rate card'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      {discardDialog}
    </>
  );
}

function HeaderFields({ form }: { form: UseFormReturn<RatecardFormValues> }) {
  const errors = form.formState.errors;
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1">
        <Label htmlFor="rc-name">Name</Label>
        <Input
          id="rc-name"
          aria-invalid={Boolean(errors.name)}
          placeholder="e.g. Standard 2026"
          {...form.register('name')}
        />
        {errors.name?.message ? (
          <p className="text-xs text-destructive">{errors.name.message}</p>
        ) : null}
      </div>
      <div className="space-y-1">
        <Label htmlFor="rc-from">Effective from</Label>
        <DatePicker
          id="rc-from"
          value={form.watch('effectiveFrom')}
          onChange={(v) =>
            form.setValue('effectiveFrom', v, { shouldValidate: true, shouldDirty: true })
          }
        />
        {errors.effectiveFrom?.message ? (
          <p className="text-xs text-destructive">{errors.effectiveFrom.message}</p>
        ) : null}
      </div>
      <div className="space-y-1">
        <Label htmlFor="rc-to">Effective to</Label>
        <DatePicker
          id="rc-to"
          value={form.watch('effectiveTo') ?? ''}
          onChange={(v) =>
            form.setValue('effectiveTo', v, { shouldValidate: true, shouldDirty: true })
          }
          placeholder="Open-ended"
          clearable
        />
        {errors.effectiveTo?.message ? (
          <p className="text-xs text-destructive">{errors.effectiveTo.message}</p>
        ) : null}
      </div>
    </div>
  );
}

function LinesEditor({
  form,
  serviceRoles,
}: {
  form: UseFormReturn<RatecardFormValues>;
  serviceRoles: ServiceRole[];
}) {
  const lineErrors = form.formState.errors.lines;
  const rootLinesError = form.formState.errors.root?.lines?.message;
  return (
    <div className="space-y-1">
      <Label>Service role rates</Label>
      <p className="text-xs text-muted-foreground">
        Every active service role must have a rate. Inactive roles are excluded.
      </p>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-32">Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-40 text-right">Rate</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {serviceRoles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-sm text-muted-foreground">
                  No active service roles. Add service roles before creating a rate card.
                </TableCell>
              </TableRow>
            ) : (
              serviceRoles.map((r, i) => {
                const rowError = lineErrors?.[i]?.rate?.message;
                return (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-xs">{r.shortCode}</TableCell>
                    <TableCell className="text-sm">{r.name}</TableCell>
                    <TableCell>
                      {/* serviceRoleId is held in RHF state via the
                          `lines.N.serviceRoleId` slot in defaultValues;
                          no DOM input needed. */}
                      <input type="hidden" {...form.register(`lines.${i}.serviceRoleId`)} />
                      <Controller
                        control={form.control}
                        name={`lines.${i}.rate`}
                        render={({ field }) => (
                          <CurrencyInput
                            value={field.value}
                            onChange={field.onChange}
                            aria-label={`Rate for ${r.name}`}
                            className={
                              rowError
                                ? 'h-8 border-destructive text-right text-sm focus-visible:ring-destructive'
                                : 'h-8 text-right text-sm'
                            }
                          />
                        )}
                      />
                      {rowError ? (
                        <p className="mt-1 text-right text-xs text-destructive">{rowError}</p>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      {rootLinesError ? <p className="text-xs text-destructive">{rootLinesError}</p> : null}
    </div>
  );
}
